import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, output, signal } from '@angular/core';
import { EvaluationService } from '@services/core/evaluation.service';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AdminQuestionFormComponent } from './admin-question-form.component';
import { confirmDeleteDialog, showHistoryDialog } from './question-dialogs';
import { parseMonths } from '../../../shared/period.utils';
import type { Question } from '../../../shared/evaluation.types';
import { asSubs, buildQuestionPayload, buildSubDraft, copyQuestions, countLabel, countSubitems, findById, hasSpecificMonths, isBadRequest, isForcedMonthlyFlag, isParentRowFlag, isPositive, replaceSubitem, shiftedList, stampForm, sumScores, typeLabelOf, wantsSubitems } from './admin-questions-helpers';
import { disableQuestionRows, enableQuestionRows, forceSource, isHistoryConflict, removeSource, showApiError, showForceError, subIdOf, toastDeleted, toggleOf } from './admin-question-status-helpers';

@Component({
  selector: 'app-admin-questions',
  templateUrl: './admin-questions.component.html',
  imports: [NgbTooltipModule, AdminQuestionFormComponent],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: true,
})
export class AdminQuestionsComponent {
  questions = input<Question[]>([])
  selectCategoryId = input<string>('')
  yearSelected = input<number>(0)
  questionSystem = input<any>(null)

  reload = output<void>()
  protected showForm = signal(false)
  protected formType = signal<'question' | 'subquestion'>('question')
  protected editingQuestion = signal<any | null>(null)

  protected monthsList = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']

  private evaluationService = inject(EvaluationService)
  private destroyRef = inject(DestroyRef)

  protected periodicityLabel(months: string): string {
    if (hasSpecificMonths(months) === true) return countLabel(months)
    if (isForcedMonthlyFlag(this.questionSystem()) === true) return 'Mensual (obligatorio)'
    return 'Mensual'
  }

  protected isSpecificMonths(months: string): boolean {
    return hasSpecificMonths(months)
  }

  protected monthNames(months: string): string {
    return parseMonths(months).map((n) => this.monthShort(n)).join(', ')
  }

  private monthShort(n: number): string {
    return this.monthsList[n - 1] || ''
  }

  protected getTotalScore(question: Question): number {
    return sumScores(asSubs(question.subitems))
  }

  protected hasSubitems(question: Question) {
    return isPositive(countSubitems(question.subitems))
  }

  protected isParentRow(question: Question) {
    return isParentRowFlag(question, this.hasSubitems(question))
  }

  typeLabel(question: Question) {
    return typeLabelOf(question)
  }

  openNew() {
    this.formType.set('question')
    this.editingQuestion.set(null)
    this.showForm.set(true)
  }

  edit(question: Question) {
    this.formType.set('question')
    this.editingQuestion.set(question)
    this.showForm.set(true)
  }

  addSub(question: Question, subquestion?: Question) {
    this.formType.set('subquestion')
    this.editingQuestion.set(buildSubDraft(question, subquestion))
    this.showForm.set(true)
  }

  onFormSave(formData: any) {
    stampForm(formData, this.selectCategoryId(), String(this.yearSelected()))
    this.persistForm(formData)
    this.showForm.set(false)
  }

  /** Persiste según sea padre o hija. */
  private persistForm(formData: any) {
    if (formData.parentId) return this.saveSubQuestion(formData)
    this.saveQuestion(formData, wantsSubitems(formData))
  }

  private saveQuestion(data: any, wantsSubs: boolean) {
    this.sendQuestions(buildQuestionPayload(copyQuestions(this.questions()), data, wantsSubs))
  }

  private saveSubQuestion(data: any) {
    const question = findById(this.questions(), String(data.parentId))
    if (question) this.storeSub(question, data)
  }

  /** Guarda subpregunta y recarga. */
  private storeSub(question: any, data: any) {
    replaceSubitem(question, data)
    this.sendSubs(this.questions())
  }

  /** Envía subpreguntas con error 400. */
  private sendSubs(payload: unknown) {
    this.evaluationService.updateQuestions(payload as Question[]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.onSaved(),
      error: (err: any) => this.onSubError(err),
    })
  }

  /** Envía preguntas y recarga. */
  private sendQuestions(payload: unknown) {
    this.evaluationService.updateQuestions(payload as Question[]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.onSaved(),
      error: (err: any) => this.onSaveError(err),
    })
  }

  /** Emite recarga. */
  private onSaved() {
    this.reload.emit()
  }

  /** Reporta error de guardado. */
  private onSaveError(err: any) {
    showApiError(err, 'No se pudo guardar')
  }

  /** Reporta error de subpregunta. */
  private onSubError(err: any) {
    if (isBadRequest(err) === true) showApiError(err, 'No se pudo actualizar')
  }

  moveUp(i: number) {
    this.silentReload(shiftedList(copyQuestions(this.questions()), i, -1, moveItemInArray))
  }

  moveDown(i: number) {
    this.silentReload(shiftedList(copyQuestions(this.questions()), i, 1, moveItemInArray))
  }

  /** Recarga sin mostrar errores. */
  private silentReload(payload: unknown) {
    this.evaluationService.updateQuestions(payload as Question[]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.onSaved())
  }

  moveSubDown(question: Question, i: number) {
    moveItemInArray(question.subitems as Question[], i, i + 1)
    this.silentReload(this.questions())
  }

  moveSubUp(question: Question, i: number) {
    moveItemInArray(question.subitems as Question[], i, i - 1)
    this.silentReload(this.questions())
  }

  confirmRemove(type: 'question' | 'subquestion', id: string, title: string) {
    confirmDeleteDialog(title, id, () => this.remove(type, id, title))
  }

  private remove(type: 'question' | 'subquestion', id: string, title: string) {
    const source = removeSource(this.evaluationService, type, id) as { pipe: any }
    source.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.onSaved(),
      error: (err: any) => this.onRemoveError(err, type, id, title),
    })
  }

  /** Deriva error de borrado. */
  private onRemoveError(err: any, type: 'question' | 'subquestion', id: string, title: string) {
    if (isHistoryConflict(err) === true) return this.showHistory(type, id, title)
    showApiError(err, 'Error al eliminar')
  }

  /** Ofrece forzar o deshabilitar. */
  private showHistory(type: 'question' | 'subquestion', id: string, title: string) {
    showHistoryDialog(title, () => this.forceDelete(type, id), () => this.disableItem(type, id))
  }

  private forceDelete(type: 'question' | 'subquestion', id: string) {
    const source = forceSource(this.evaluationService, type, id) as { pipe: any }
    source.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.onForceDeleted(),
      error: (err: any) => showForceError(err),
    })
  }

  /** Confirma borrado forzado. */
  private onForceDeleted() {
    toastDeleted()
    this.reload.emit()
  }

  private disableItem(type: 'question' | 'subquestion', id: string) {
    if (type === 'question') return this.disableQuestion(id)
    this.disableSub(id)
  }

  /** Deshabilita pregunta e hijas. */
  private disableQuestion(id: string) {
    this.sendQuestions(disableQuestionRows(this.questions(), id))
  }

  /** Deshabilita subpregunta. */
  private disableSub(id: string) {
    this.evaluationService.toggleSubQuestionEnabled(id, false).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.onSaved(),
      error: (err: any) => showApiError(err, 'Error al cambiar estado'),
    })
  }

  toggleQuestionEnabled(question: Question) {
    const enabled = toggleOf(question)
    this.sendQuestions(enableQuestionRows(this.questions(), question.id as string, enabled))
  }

  toggleSubQuestionEnabled(subquestion: Question) {
    this.setSubEnabled(subIdOf(subquestion), toggleOf(subquestion))
  }

  /** Cambia estado de subpregunta. */
  private setSubEnabled(id: string, enabled: boolean) {
    this.evaluationService.toggleSubQuestionEnabled(id, enabled).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.onSaved(),
      error: (err: any) => showApiError(err, 'Error al cambiar estado'),
    })
  }
}
